import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Globe from './Globe';
import UniverseNetwork from './UniverseNetwork';

function HeroSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/directory?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <section className="relative min-h-[600px] md:min-h-[750px] flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100 via-background-50 to-background-50" />
        <div className="absolute top-1/4 -left-24 w-96 h-96 rounded-full bg-primary-500/20 blur-[130px]" />
        <div className="absolute bottom-0 -right-24 w-96 h-96 rounded-full bg-accent-500/15 blur-[130px]" />
        <div className="absolute top-1/3 right-1/3 w-72 h-72 rounded-full bg-primary-400/10 blur-[100px]" />

        {/* Floating glow particles */}
        <span
          className="absolute hero-particle rounded-full bg-primary-400/60 blur-[2px]"
          style={{ width: 8, height: 8, top: '22%', left: '18%', animationDelay: '0s' }}
        />
        <span
          className="absolute hero-particle rounded-full bg-accent-400/60 blur-[2px]"
          style={{ width: 6, height: 6, top: '34%', left: '78%', animationDelay: '2s' }}
        />
        <span
          className="absolute hero-particle rounded-full bg-primary-300/50 blur-[3px]"
          style={{ width: 10, height: 10, top: '58%', left: '12%', animationDelay: '4s' }}
        />
        <span
          className="absolute hero-particle rounded-full bg-accent-300/50 blur-[3px]"
          style={{ width: 7, height: 7, top: '66%', left: '70%', animationDelay: '6s' }}
        />
        <span
          className="absolute hero-particle rounded-full bg-primary-500/40 blur-[2px]"
          style={{ width: 5, height: 5, top: '44%', left: '88%', animationDelay: '3s' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left: text */}
          <div className="relative text-center lg:text-left">
            {/* Animated universe network behind the text */}
            <UniverseNetwork />

            {/* Orbiting particles behind the text */}
            <span className="hero-orbit-wrap" style={{ ['--orbit-scale' as string]: '0.55', ['--orbit-tilt' as string]: '8deg' }}>
              <span
                className="hero-orbit-particle text-primary-400/70 bg-primary-400/70"
                style={{
                  width: 6,
                  height: 6,
                  marginLeft: -3,
                  marginTop: -3,
                  ['--orbit-radius' as string]: '170px',
                  ['--orbit-duration' as string]: '11s',
                  ['--orbit-delay' as string]: '0s',
                }}
              />
            </span>
            <span className="hero-orbit-wrap" style={{ ['--orbit-scale' as string]: '0.7', ['--orbit-tilt' as string]: '-12deg' }}>
              <span
                className="hero-orbit-particle text-accent-400/70 bg-accent-400/70"
                style={{
                  width: 5,
                  height: 5,
                  marginLeft: -2.5,
                  marginTop: -2.5,
                  ['--orbit-radius' as string]: '220px',
                  ['--orbit-duration' as string]: '16s',
                  ['--orbit-delay' as string]: '2s',
                }}
              />
            </span>
            <span className="hero-orbit-wrap" style={{ ['--orbit-scale' as string]: '0.5', ['--orbit-tilt' as string]: '15deg' }}>
              <span
                className="hero-orbit-particle text-primary-300/60 bg-primary-300/60"
                style={{
                  width: 4,
                  height: 4,
                  marginLeft: -2,
                  marginTop: -2,
                  ['--orbit-radius' as string]: '150px',
                  ['--orbit-duration' as string]: '9s',
                  ['--orbit-delay' as string]: '4s',
                }}
              />
            </span>
            <span className="hero-orbit-wrap" style={{ ['--orbit-scale' as string]: '0.65', ['--orbit-tilt' as string]: '-6deg' }}>
              <span
                className="hero-orbit-particle text-accent-300/60 bg-accent-300/60"
                style={{
                  width: 5,
                  height: 5,
                  marginLeft: -2.5,
                  marginTop: -2.5,
                  ['--orbit-radius' as string]: '250px',
                  ['--orbit-duration' as string]: '20s',
                  ['--orbit-delay' as string]: '6s',
                }}
              />
            </span>
            <span className="hero-orbit-wrap" style={{ ['--orbit-scale' as string]: '0.6', ['--orbit-tilt' as string]: '10deg' }}>
              <span
                className="hero-orbit-particle text-primary-400/50 bg-primary-400/50"
                style={{
                  width: 7,
                  height: 7,
                  marginLeft: -3.5,
                  marginTop: -3.5,
                  ['--orbit-radius' as string]: '190px',
                  ['--orbit-duration' as string]: '13s',
                  ['--orbit-delay' as string]: '8s',
                }}
              />
            </span>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-400/30 mb-8">
              <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
              <span className="text-sm font-medium text-primary-300">
                Le futur commence maintenant
              </span>
            </div>

            <h1 className="font-heading font-bold text-4xl md:text-6xl lg:text-7xl text-foreground-50 leading-tight mb-6 drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
              World<span className="text-gradient-primary">Apeiron</span>
              <br />
              <span className="text-3xl md:text-5xl lg:text-6xl">Business</span>
            </h1>

            <p className="text-lg md:text-xl text-foreground-100 max-w-2xl mx-auto lg:mx-0 mb-4 leading-relaxed drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]">
              Une seule plateforme. Un monde de possibilités.
            </p>
            <p className="text-base md:text-lg text-foreground-200 max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]">
              Connecter les talents, les entreprises et la richesse culturelle africaine au reste du
              monde, pour bâtir un avenir sans limites.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto lg:mx-0 mb-10">
              <div className="flex items-center bg-background-100/90 backdrop-blur-md border border-background-300/60 rounded-lg overflow-hidden">
                <div className="w-12 h-12 flex items-center justify-center shrink-0">
                  <i className="ri-search-line text-lg text-foreground-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher une entreprise, une catégorie, une ville..."
                  className="flex-1 bg-transparent text-base text-foreground-50 placeholder:text-foreground-400 py-3 outline-none"
                />
                <button
                  type="submit"
                  className="px-5 py-3 bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors shrink-0 whitespace-nowrap"
                >
                  Rechercher
                </button>
              </div>
            </form>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <button
                onClick={() => navigate('/directory')}
                className="px-8 py-3.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap text-sm md:text-base"
              >
                Explorer l&apos;annuaire
              </button>
              <button
                onClick={() => navigate('/auth/register')}
                className="px-8 py-3.5 border border-foreground-400/50 text-foreground-50 font-semibold rounded-lg hover:bg-foreground-50/10 transition-colors whitespace-nowrap text-sm md:text-base"
              >
                Inscrire mon entreprise
              </button>
            </div>
          </div>

          {/* Right: rotating globe */}
          <div className="flex justify-center lg:justify-end">
            <Globe className="w-56 h-56 md:w-80 md:h-80 lg:w-[420px] lg:h-[420px]" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;